import { TeamDeals, TeamDeal } from '../../models/dto/team-deal';
import {
  Action,
  Selector,
  State,
  StateContext,
  createSelector
} from '@ngxs/store';
import { EntityActions as EA } from '../entity/entity.action';
import { EntityState } from '../entity/entity.state';
import { Injectable } from '@angular/core';
import { LenderUserDealsService, TeamDealsService } from '../../modules/team-deals/services/team-deals.service';
import { TeamDealsActions as TDA } from './team-deals.action';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { compose, patch, updateItem } from '@ngxs/store/operators';

// Inherit the parent state model with correct entity type.
export class TeamDealsStateModel {
  funded: TeamDeal[];
  contracts: TeamDeal[];
  offers: TeamDeal[];
  loading: boolean;
  selectedUserIds: number[];
  error?: any;
}

@State<TeamDealsStateModel>({
	name: 'teamDeals',
	defaults: {
    funded: [],
    contracts: [],
    offers: [],
    loading: false,
    selectedUserIds: [],
  }
})

@Injectable()
export class TeamDealsState extends EntityState<TeamDeals> {

  // Contracts not marked as closing today.
  @Selector()
	static contracts(includeClosingToday: boolean) {
    return createSelector([TeamDealsState], state => {
      const { teamDeals } = state;
      const contracts = teamDeals?.contracts;
      return includeClosingToday ? contracts :
             contracts.filter( d => !d.dealFlagId );
    });
  }

  @Selector()
  static funded(state: TeamDealsStateModel) {
    return state.funded;
  }

  @Selector()
	static closingToday(state: TeamDealsStateModel) {
    return state.contracts?.filter( d => !!d.dealFlagId );
  }

  @Selector()
  static loading(state: TeamDealsStateModel) {
    return state.loading;
  }

  @Selector()
  static offerDeals(state: TeamDealsStateModel) {
    const selectedUserIds = state?.selectedUserIds;
    return selectedUserIds?.length
           ? state.offers?.filter( d => selectedUserIds.includes(d.lenderUserId) )
           : state.offers;
  }

  // DI creates a concrete service that implements AbstractCrudService for
  // use by the parent EntityState.
	constructor(
    private concreteEntityService: TeamDealsService,
    private lenderUserDealsService: LenderUserDealsService,
  ) {
    super(
      concreteEntityService,
      // Optional expiresIn argument as `(5 * 60 * 1000)`. 5 minute default.
    );
  }

  // If you only need generic CRUD action and selectors, you're done :).
  // Or you can implement additional stuff specific to this store using its
  // actions.ts file.
  @Action(TDA.GetDeals)
	faGet(ctx: StateContext<TeamDealsStateModel>, payload: EA.Get) {
    ctx.patchState({
      loading: true,
    });
    return this.concreteEntityService.get(payload.id).subscribe( response => {
      ctx.patchState({
        funded: response.funded || [],
        contracts: response.contracts || [],
        offers: response.offers || [],
        loading: false,
      });
    });
  }

  @Action(TDA.GetLenderUserDeals)
  fmGet(ctx: StateContext<TeamDealsStateModel>, payload: TDA.GetLenderUserDeals) {
    return this.lenderUserDealsService.get(payload.id)
      .pipe(
        catchError( error => {
          ctx.patchState({
            loading: false,
            error
          });
          return of(error);
        })
      )
      .subscribe( response => {
      ctx.patchState({
        funded: response.funded || [],
        contracts: response.contracts || [],
        offers: response.offers || [],
        loading: false,
      });
    });
  }

  @Action(TDA.TeamDealFlagAdded)
  teamDealFlagAdded(ctx: StateContext<TeamDealsStateModel>, payload: TDA.TeamDealFlagAdded) {
    const dealFlag = payload.dealFlag;
    ctx.setState(patch({
      contracts: updateItem( (deal: TeamDeal) =>  deal.id === dealFlag.dealId, patch({
        dealFlagId: dealFlag.id,
        dealFlag: dealFlag.flag
      }))
    }));
  }

  @Action(TDA.TeamDealFlagRemoved)
  teamDealFlagRemoved(ctx: StateContext<TeamDealsStateModel>, { dealId }) {
    ctx.setState(patch({
      contracts: updateItem( (deal: TeamDeal) =>  deal.id === dealId, patch({
        dealFlagId: null,
        dealFlag: null,
      }))
    }));
  }

  @Action(TDA.SetSelectedUserIds)
  setSelectedUserIds(ctx: StateContext<TeamDealsStateModel>, { userIds }) {
    ctx.patchState({
      selectedUserIds: userIds,
    });
  }

  @Action(TDA.UpdateDealNoteContent)
  updateDealNoteContent(ctx: StateContext<TeamDealsStateModel>, { dealNote }) {
    const { dealId, content } = dealNote;
    ctx.setState(patch({
      contracts: this.patchDealNote(dealId, content),
      funded: this.patchDealNote(dealId, content),
      offers: this.patchDealNote(dealId, content),
    }));
  }

  @Action(TDA.UpdateBorrowerNoteContent)
  updateBorrowerNoteContent(
    ctx: StateContext<TeamDealsStateModel>,
    {note}
  ) {
    const { borrowerId, content } = note;
    if(!!borrowerId && !!content) {
      const { contracts, funded, offers } = ctx.getState();
      ctx.setState(patch({
        contracts: this.patchBorrowerNote(contracts, note.borrowerId, note.content),
        funded: this.patchBorrowerNote(funded, note.borrowerId, note.content),
        offers: this.patchBorrowerNote(offers, note.borrowerId, note.content),
      }));
    }
  }

  private patchBorrowerNote(deals, borrowerId, borrowerNoteContent) {
    const indexes = deals.reduce( (res, cur, index) => {
      return cur.borrowerId === borrowerId
             ? [...res, index]
             : res;
    }, []);
    return compose<TeamDeal[]>(...indexes.map( (i) => updateItem<TeamDeal>(i, patch({
      latestBorrowerNote: borrowerNoteContent,
    }))));
  }

  private patchDealNote(dealId, dealNoteContent) {
    return updateItem( (d: TeamDeal) => d.id === dealId, patch({
      latestDealNote: dealNoteContent,
    }));
  }

}
